// Per-hotel-program editorial insight surfaced on every curated hotel
// result. Same shape + intent as flightInsights.js — hand-curated,
// opinionated takes on each loyalty program so the user understands
// WHY this hotel is being recommended, what the product is actually
// like at this property tier, and exactly how to transfer points in.

export const HOTEL_PROGRAM_INSIGHTS = {
  'World of Hyatt': {
    name: 'World of Hyatt',
    sweetSpot: 'Category 1-4 properties (3,500 - 17,000 pts/night) — the best points-to-cash ratio of any major hotel program. Park Hyatt redemptions at Cat 7 (30K) regularly hit 3+ cpp.',
    pros: [
      'Smallest devaluations of any major program — published award chart, predictable pricing',
      'Globalist status (60 nights/year, or via Chase loophole) = guaranteed 4 PM checkout, suite upgrades, free breakfast for 2',
      'Free night certificates at Cat 1-4 / Cat 1-7 from the credit card are some of the most valuable loyalty perks in the industry',
      'Award nights count toward elite status (5 nights / 2 awards earned)',
    ],
    cons: [
      'Smallest footprint of the major programs — many cities have 0-2 Hyatt options',
      'Chase UR is the ONLY major transfer partner — if you don\'t have Chase, you can\'t easily top up',
      'Hyatt House / Place properties feel like extended-stay (fine but not "vacation hotel" energy)',
    ],
    productNotes: 'Park Hyatt = ultra-luxury (Park Hyatt Tokyo, Vienna, Paris-Vendôme). Andaz = lifestyle-luxury (NYC, Tokyo, Mayakoba). Grand Hyatt = upscale business. Hyatt Regency = solid 4-star. Skip Hyatt Place / House unless price-sensitive.',
    whyRecommend: 'Hyatt is the best hotel points program, full stop. If your destination has a Hyatt and you have Chase UR, this should usually be your first redemption considered.',
  },
  'Hilton Honors': {
    name: 'Hilton Honors',
    sweetSpot: 'Off-peak Conrads and Waldorf-Astorias in mid-tier markets (95K-150K/night for properties that go for $700+ cash). The Maldives Waldorf at 150K is genuinely insane value.',
    pros: [
      'Largest footprint of any major chain — hotels in markets no other program touches',
      '5th night free on award stays (massive value on long stays)',
      'Diamond status = free breakfast at most properties + executive lounge access',
      'Amex MR transfers 1:2 — gets you a lot of Hilton points fast (offset by the program\'s soft devaluations)',
    ],
    cons: [
      'No published award chart — pricing is dynamic and can change overnight',
      'Diamond status is "easy to get" via the Aspire card, so the perk is diluted (lounges are often full)',
      'Resort fees are still charged on award stays at most US properties',
      'Suite upgrades for elites are NOT confirmed at booking — front-desk lottery',
    ],
    productNotes: 'Waldorf-Astoria = top tier (NYC, Maldives, Beverly Hills). Conrad = luxury-business (Tokyo, Bali, Singapore). Hilton = mid-market dependable. Embassy Suites = great for families (free hot breakfast, evening reception). Skip Hilton Garden Inn / Hampton unless on a budget.',
    whyRecommend: 'When you need a brand-name luxury hotel in an unusual market (Indonesia, smaller European capitals), Hilton has options that Hyatt/Marriott don\'t. Aspire card free night is essentially free $1,000 every year.',
  },
  'Marriott Bonvoy': {
    name: 'Marriott Bonvoy',
    sweetSpot: 'Marriott Vacation Club properties + Cat 4 brand hotels (under 35K/night) in secondary European cities. The Marriott Vacation Club Florence/Vienna are absurd value.',
    pros: [
      'Biggest footprint of all — almost every city has a Marriott',
      '5th night free on award stays',
      'Free night certificates from credit cards can be topped up with 15K points to use anywhere',
      'Top-end properties (Ritz-Carlton, St. Regis, Edition) are world-class',
    ],
    cons: [
      'Dynamic pricing on most properties post-2022 — peak/off-peak abolished',
      'Award prices have crept up steadily — points value is ~0.6-0.8 cpp typically',
      'Ambassador status is hard to reach (100 nights + $23K spend) and Titanium (75 nights) benefits are inconsistent',
      'Suite upgrades for elites are best-effort, not confirmed',
    ],
    productNotes: 'Ritz-Carlton + Edition + St. Regis = ultra-luxury, Luxury Collection = boutique luxury (often historic buildings). JW Marriott = upscale-business. Marriott + Westin + Sheraton = solid 4-star. Renaissance / Le Méridien = lifestyle. Skip Courtyard / Residence Inn unless on the road for work.',
    whyRecommend: 'Marriott\'s value isn\'t in pure points — it\'s in the footprint. When Hyatt and Hilton don\'t have your city, Marriott will. Plus the 5th-night-free + free-night-certificate combo can extend trips affordably.',
  },
  'IHG One Rewards': {
    name: 'IHG One Rewards',
    sweetSpot: 'InterContinental hotels in Asia + Vignette/Kimpton boutique properties (especially the InterCon Phú Quốc, Bali, Bangkok — 80-100K/night for $400+ rooms).',
    pros: [
      '4th night free benefit from the Chase IHG Premier card (very valuable)',
      'No published chart but pricing tends to be reasonable at upper-tier properties',
      'Kimpton properties (now under IHG) are genuinely cool boutique hotels with free wine hour + dog-friendly policies',
      'Chase UR transfers 1:1 — easy to top up',
    ],
    cons: [
      'Chase UR exclusive on the bank-transfer side',
      'Holiday Inn / Holiday Inn Express stays earn diminishing points (most stays earn 5x base + bonuses)',
      'Top-tier elite (Diamond) benefits are inconsistent — breakfast not guaranteed',
      'No 5th night free on awards (unlike Hilton/Marriott)',
    ],
    productNotes: 'Six Senses + Regent = ultra-luxury (newly acquired). InterContinental = upscale-business with proper service. Kimpton = boutique lifestyle (lobby wine hour + pet-friendly). Vignette = European boutique. Hotel Indigo = neighborhood-themed. Skip Holiday Inn / Crowne Plaza unless price-sensitive.',
    whyRecommend: 'For luxury in Asia (Phú Quốc, Bali, Bangkok, Tokyo) at points prices Hyatt/Marriott can\'t match. The Chase IHG Premier card 4th-night-free turns a 4-night stay into 3 nights of points effectively.',
  },
  'Accor Live Limitless': {
    name: 'Accor Live Limitless (ALL)',
    sweetSpot: 'Sofitel + Fairmont properties in Europe — 50% discount when paying with ALL points means a 200€ room becomes 100€ + 4,000 points. Stacks with Accor Plus subscription for further savings.',
    pros: [
      'Strongest European footprint of any hotel program — Sofitel, Fairmont, Pullman, Mövenpick',
      'Points work as currency (€1 = 50 pts) — flexible, predictable, never devalued',
      'Capital One Venture and Citi ThankYou transfer 2:1 — accessible from US currencies',
      'Plus membership = free welcome drink + 1 free meal per stay at participating properties',
    ],
    cons: [
      '2:1 transfer ratio means you burn through US currency fast — works best for ALL "ALL" stays not occasional ones',
      'No award chart, no free-night certificates from US credit cards',
      'Elite status benefits (Platinum/Diamond) are mostly recognition, not concrete perks',
    ],
    productNotes: 'Raffles = ultra-luxury (Singapore, Paris, Istanbul). Fairmont = grand-luxury (Banff Springs, Empress, Savoy). Sofitel = French upscale (Sofitel Legend in Paris, Cairo). Pullman / Mercure = mid-market. SO/ + 25hours + JO&JOE = lifestyle.',
    whyRecommend: 'If your trip is primarily Europe (especially France, Spain, Italy), Accor properties dominate. Capital One + Citi transfers unlock this from US currency, which Hyatt/Hilton can\'t do.',
  },
  'Wyndham Rewards': {
    name: 'Wyndham Rewards',
    sweetSpot: 'Vacasa vacation rentals (15K/night for full beach houses) and Caesars Rewards properties (Vegas suites at 30K/night for $400+ rooms).',
    pros: [
      'Vacasa partnership — book entire vacation rental houses with points (unique among hotel programs)',
      'Flat-rate award chart (7.5K / 15K / 30K) means predictable pricing',
      'Capital One transfers 1:1, Citi TY also',
      'Caesars Rewards properties bookable — Vegas / Atlantic City suites',
    ],
    cons: [
      'Brand portfolio skews economy/midscale (Days Inn, Super 8, Ramada) — limited luxury options',
      'Vacasa selection varies wildly — book early for peak summer/winter dates',
      'Elite status benefits are limited compared to other programs',
    ],
    productNotes: 'Vacasa = full-home rentals (beach houses, ski cabins). Caesars = Vegas + AC strip resorts. Wyndham Grand = upscale (rare). Most other Wyndham brands = solid budget.',
    whyRecommend: 'When you want a HOUSE not a room — group trips, family vacations, anniversaries with rented kitchens — Wyndham + Vacasa is the only hotel-points way to do it.',
  },
  'Boutique / Other': {
    name: 'Boutique / Other',
    sweetSpot: 'Sometimes the best stay in a city isn\'t a chain — local boutiques are bookable with cash + portal points (Amex Travel, Chase Travel, Capital One Travel) at fixed cents-per-point.',
    pros: [
      'Unique properties no chain has — local character, art-filled common spaces, personalized service',
      'Amex Fine Hotels & Resorts adds $100 hotel credit + breakfast for 2 + room upgrade for Platinum holders at participating properties',
      'Chase Sapphire Reserve\'s "Edit" hotel collection has similar perks at no extra cost',
    ],
    cons: [
      'Cash + portal-points only — no transferable currency converts to "boutique"',
      'Cancellation policies often stricter than chain hotels',
      'No 5th-night-free or other loyalty stacking',
    ],
    productNotes: 'Vary wildly. Read reviews carefully — boutiques are higher-variance than chains. Look for properties with consistent recent 4.5+ on TripAdvisor + booking.com.',
    whyRecommend: 'For the *one special hotel* of a trip — anniversary stay, honeymoon, an iconic property like Hotel Esencia in Tulum or Faena in Miami — a boutique often beats any chain. Just pay cash (or portal points) and use a card with travel insurance + earning multipliers.',
  },
};

// Transferable currency → hotel program. Same shape as TRANSFER_PATHS in
// flightInsights, but for hotels. Used to render the "how to convert" panel.
export const HOTEL_TRANSFER_PATHS = {
  'World of Hyatt': [
    { source: 'chase_ur', ratio: '1:1', time: 'Instant', notes: 'Chase UR exclusive — the ONLY transferable currency path' },
    { source: 'bilt', ratio: '1:1', time: 'Instant', notes: 'Bilt is the only NON-Chase route' },
  ],
  'Hilton Honors': [
    { source: 'amex_mr', ratio: '1:2', time: 'Instant', notes: '1:2 ratio means 50K MR = 100K Hilton' },
    { source: 'bilt', ratio: '1:2', time: 'Instant' },
  ],
  'Marriott Bonvoy': [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
    { source: 'chase_ur', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  'IHG One Rewards': [
    { source: 'chase_ur', ratio: '1:1', time: 'Instant', notes: 'Chase UR exclusive among bank currencies' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  'Accor Live Limitless (ALL)': [
    { source: 'capital_one', ratio: '2:1', time: 'Instant', notes: '2:1 ratio — burns currency fast' },
    { source: 'citi_ty', ratio: '2:1', time: 'Instant', notes: '2:1 ratio — burns currency fast' },
  ],
  'Wyndham Rewards': [
    { source: 'capital_one', ratio: '1:1', time: 'Instant' },
    { source: 'citi_ty', ratio: '1:1', time: 'Instant' },
  ],
};

// Hotel program → award booking URL
export const HOTEL_BOOKING_URLS = {
  'World of Hyatt': 'https://www.hyatt.com/explore-hotels',
  'Hilton Honors': 'https://www.hilton.com/en/hilton-honors/',
  'Marriott Bonvoy': 'https://www.marriott.com/loyalty/redeem.mi',
  'IHG One Rewards': 'https://www.ihg.com/onerewards',
  'Accor Live Limitless (ALL)': 'https://all.accor.com/',
  'Wyndham Rewards': 'https://www.wyndhamrewards.com/',
};

// Generic transfer-then-book steps for hotels. Same template as flights but
// adjusted for hotel reservation patterns (taxes are usually NOT charged on
// award nights, unlike flights).
export function buildHotelTransferSteps(programName, bestSource = 'amex_mr') {
  const sourceLabel = ({
    amex_mr: 'Amex MR', chase_ur: 'Chase UR', capital_one: 'Capital One Venture',
    citi_ty: 'Citi ThankYou', bilt: 'Bilt',
  })[bestSource] || bestSource;
  return [
    `Search ${programName} directly to confirm the property + room category has award availability on your dates`,
    `Check the cents-per-point value — if cash rate ÷ points needed × 100 < 1.5 cpp, paying cash is often better`,
    `Open your ${sourceLabel} transfer portal and select ${programName} as the partner`,
    `Transfer the exact number of points you need (one-way and irreversible — never transfer "just in case")`,
    `Most hotel transfers are instant. Book the award immediately after points land`,
    `On award stays, no taxes are charged at most properties — but resort fees still apply at US resorts (typically $35-50/night)`,
  ];
}

const PROGRAM_KEY_ALIASES = {
  'hyatt': 'World of Hyatt',
  'worldofhyatt': 'World of Hyatt',
  'hilton': 'Hilton Honors',
  'hiltonhonors': 'Hilton Honors',
  'marriott': 'Marriott Bonvoy',
  'marriottbonvoy': 'Marriott Bonvoy',
  'bonvoy': 'Marriott Bonvoy',
  'ihg': 'IHG One Rewards',
  'ihgonerewards': 'IHG One Rewards',
  'accor': 'Accor Live Limitless (ALL)',
  'all': 'Accor Live Limitless (ALL)',
  'wyndham': 'Wyndham Rewards',
  'wyndhamrewards': 'Wyndham Rewards',
};

export function getHotelInsight(programName) {
  if (!programName) return HOTEL_PROGRAM_INSIGHTS['Boutique / Other'];
  // Direct match
  if (HOTEL_PROGRAM_INSIGHTS[programName]) return HOTEL_PROGRAM_INSIGHTS[programName];
  // Alias / fuzzy
  const key = String(programName).toLowerCase().replace(/[^a-z]/g, '');
  const resolved = PROGRAM_KEY_ALIASES[key];
  if (resolved && HOTEL_PROGRAM_INSIGHTS[resolved]) return HOTEL_PROGRAM_INSIGHTS[resolved];
  // Substring match
  for (const known of Object.keys(HOTEL_PROGRAM_INSIGHTS)) {
    if (programName.toLowerCase().includes(known.toLowerCase().split(' ')[0])) {
      return HOTEL_PROGRAM_INSIGHTS[known];
    }
  }
  return HOTEL_PROGRAM_INSIGHTS['Boutique / Other'];
}
